import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router';
import { api, query, withLocationFullConfirm } from '../../lib/api';
import type { Appointment, Slot, Therapist } from '../../lib/types';
import { MODE_LABELS, PAYMENT_LABELS, STATUS_LABELS, addDays, clinicDate, formatDateTime, formatPrice, formatTime, groupByClinicDay, nowOnce } from '../../lib/format';
import { Alert, Dialog, ErrorMessage, Field, Loading, PageTitle, SelectField } from '../../components/ui';
import { Pii } from '../../components/Pii';

export function AppointmentsPage() {
  const [params] = useSearchParams();
  const appointments = useQuery({ queryKey: ['appointments', 'mine'], queryFn: () => api.get<Appointment[]>('/api/v1/appointments/mine') });
  const [now] = useState(nowOnce);
  if (!appointments.data) return <Loading />;

  const upcoming = appointments.data.filter((a) => new Date(a.endsAt).getTime() >= now && a.status !== 'Cancelled').reverse();
  const past = appointments.data.filter((a) => !upcoming.includes(a));

  return (
    <>
      <PageTitle>Moje wizyty</PageTitle>
      {params.get('zarezerwowano') && <Alert kind="success">Wizyta została zarezerwowana. Potwierdzenie wysłaliśmy e-mailem.</Alert>}
      <p>
        <Link className="button" to="/rezerwacja">
          Umów nową wizytę
        </Link>
      </p>
      <section aria-labelledby="upcoming-title">
        <h2 id="upcoming-title">Nadchodzące</h2>
        {upcoming.length === 0 ? <p>Nie masz zaplanowanych wizyt.</p> : upcoming.map((a) => <AppointmentCard key={a.id} appointment={a} />)}
      </section>
      <section aria-labelledby="past-title">
        <h2 id="past-title">Historia</h2>
        {past.length === 0 ? <p>Brak wcześniejszych wizyt.</p> : past.map((a) => <AppointmentCard key={a.id} appointment={a} />)}
      </section>
    </>
  );
}

function AppointmentCard({ appointment: a }: { appointment: Appointment }) {
  const client = useQueryClient();
  const [cancelOpen, setCancelOpen] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const [reason, setReason] = useState('');
  const refresh = () => client.invalidateQueries({ queryKey: ['appointments'] });

  const pay = useMutation({
    mutationFn: () => api.post<{ redirectUrl: string }>(`/api/v1/appointments/${a.id}/payment`),
    onSuccess: ({ redirectUrl }) => window.location.assign(redirectUrl),
  });
  const awaitingPayment = a.paymentStatus === 'Awaiting' && a.status !== 'Cancelled';

  const cancel = useMutation({
    mutationFn: () => api.post(`/api/v1/appointments/${a.id}/cancel`, { reason: reason || null }),
    onSuccess: async () => {
      setCancelOpen(false);
      await refresh();
    },
  });

  return (
    <article className={`appointment status-${a.status.toLowerCase()}`}>
      <h3>{formatDateTime(a.startsAt)}</h3>
      <p>
        {a.serviceName} · <Pii>{a.therapistName}</Pii> · {MODE_LABELS[a.mode]}
        {a.roomName && ` · ${a.roomName}`}
      </p>
      <p className="status">
        {STATUS_LABELS[a.status]}
        {a.paymentStatus !== 'NotRequired' && ` · ${PAYMENT_LABELS[a.paymentStatus]}`}
        {a.price != null && a.paymentStatus !== 'NotRequired' && ` (${formatPrice(a.price)})`}
      </p>
      {awaitingPayment && a.paymentDueAt && (
        <Alert kind="warning">Opłać wizytę do {formatDateTime(a.paymentDueAt)} – nieopłacona wizyta zostanie automatycznie odwołana.</Alert>
      )}
      <ErrorMessage error={pay.error} />
      <div className="button-row">
        {a.meetingUrl && a.status !== 'Cancelled' && (
          <a className="button" href={a.meetingUrl} target="_blank" rel="noopener noreferrer">
            Dołącz do wizyty online
          </a>
        )}
        {awaitingPayment && (
          <button type="button" onClick={() => pay.mutate()} disabled={pay.isPending}>
            Opłać wizytę
          </button>
        )}
        <a className="button secondary" href={`/api/v1/appointments/${a.id}/ics`}>
          Dodaj do kalendarza
        </a>
        {a.canChange && (
          <>
            <button type="button" className="secondary" onClick={() => setMoveOpen(true)}>
              Zmień termin lub terapeutę
            </button>
            <button type="button" className="danger" onClick={() => setCancelOpen(true)}>
              Odwołaj
            </button>
          </>
        )}
      </div>
      <Dialog open={cancelOpen} title="Odwołać wizytę?" onClose={() => setCancelOpen(false)}>
        <p>{formatDateTime(a.startsAt)}</p>
        <Field label="Powód (opcjonalnie, widoczny dla terapeuty)" name="reason" value={reason} onChange={setReason} maxLength={1000} />
        <ErrorMessage error={cancel.error} />
        <div className="button-row">
          <button type="button" className="danger" onClick={() => cancel.mutate()} disabled={cancel.isPending}>
            Odwołaj wizytę
          </button>
          <button type="button" className="secondary" onClick={() => setCancelOpen(false)}>
            Zostaw
          </button>
        </div>
      </Dialog>
      {moveOpen && <RescheduleDialog appointment={a} onClose={() => setMoveOpen(false)} onDone={refresh} />}
    </article>
  );
}

/** Moves a visit to a free slot – of the same therapist or of another one offering the same service. */
export function RescheduleDialog({ appointment, onClose, onDone }: { appointment: Appointment; onClose: () => void; onDone: () => Promise<unknown> }) {
  const [from, setFrom] = useState(() => addDays(clinicDate(new Date()), 1));
  const [therapistId, setTherapistId] = useState(appointment.therapistId);
  const therapists = useQuery({ queryKey: ['therapists'], queryFn: () => api.get<Therapist[]>('/api/v1/therapists') });
  const offering = therapists.data?.filter((t) => t.id === appointment.therapistId || t.services.some((s) => s.id === appointment.serviceId)) ?? [];
  const newPrice = therapists.data?.find((t) => t.id === therapistId)?.services.find((s) => s.id === appointment.serviceId)?.price;
  const slots = useQuery({
    queryKey: ['slots', appointment.serviceId, therapistId, from],
    queryFn: () => api.get<Slot[]>(`/api/v1/slots${query({ serviceId: appointment.serviceId, therapistId, from, to: addDays(from, 6) })}`),
  });
  const move = useMutation({
    // Only staff get the question about a location with all rooms taken; patients do not get such a slot.
    mutationFn: (slot: Slot) =>
      withLocationFullConfirm((confirmLocationFull) =>
        api.post(`/api/v1/appointments/${appointment.id}/reschedule`, {
          startsAt: slot.startsAt,
          therapistId: therapistId === appointment.therapistId ? null : therapistId,
          confirmLocationFull,
        }),
      ),
    onSuccess: async () => {
      await onDone();
      onClose();
    },
  });

  return (
    <Dialog open title="Zmień termin wizyty" onClose={onClose}>
      {offering.length > 1 && (
        <SelectField pii
          label="Terapeuta"
          value={therapistId}
          onChange={setTherapistId}
          options={offering.map((t) => ({ value: t.id, label: `${t.firstName} ${t.lastName}${t.id === appointment.therapistId ? ' (obecny)' : ''}` }))}
        />
      )}
      {therapistId !== appointment.therapistId && appointment.paymentStatus === 'Awaiting' && newPrice != null && newPrice !== appointment.price && (
        <p className="hint">U tego terapeuty wizyta kosztuje {formatPrice(newPrice)}.</p>
      )}
      <div className="button-row">
        <button type="button" className="secondary" onClick={() => setFrom(addDays(from, -7))} disabled={from <= clinicDate(new Date())}>
          ← Wcześniej
        </button>
        <button type="button" className="secondary" onClick={() => setFrom(addDays(from, 7))}>
          Później →
        </button>
      </div>
      <ErrorMessage error={move.error ?? slots.error} />
      {slots.data &&
        groupByClinicDay(slots.data).map(([day, items]) => (
          <div key={day}>
            <h3>{day}</h3>
            <ul className="slot-list">
              {items.map((s) => (
                <li key={s.startsAt}>
                  <button type="button" className="slot" onClick={() => move.mutate(s)} disabled={move.isPending}>
                    {formatTime(s.startsAt)}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ))}
      {slots.data?.length === 0 && <p>Brak wolnych terminów w tym tygodniu.</p>}
      <button type="button" className="secondary" onClick={onClose}>
        Zamknij
      </button>
    </Dialog>
  );
}
